"use client";

import {
  optimizeImageUrlHeight,
  optimizeImageUrlWidth,
} from "@faire/web/common/images/optimizeImages";
import { ScrollStore } from "@faire/web/common/ScrollStore";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { trackEvent } from "@faire/web/common/trackEvent";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { useDetectRefScroll } from "@faire/web/ui/hooks/useDetectScroll";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { trackSlateDesignSystemUiModalShowView } from "@faire/web-api/events/slateDesignSystem/view/uiModalShow";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";

import { ITheme } from "../Theme";

import { BaseModal } from "./BaseModal";
import { ModalNavigationAbsolute } from "./ModalNavigation/ModalNavigationAbsolute";
import { ModalNavigationFixed } from "./ModalNavigation/ModalNavigationFixed";
import { AlignValues, JustifyValues, ModalProps } from "./types";
import { useGetModalSizing } from "./useGetModalSizing";

/**
 * Default wrapping div for the children of the `Modal`.
 * Exported so that modifications can be made to this for
 * cases where smaller content can be centered by wrapping
 * it in a parent component.
 *
 * @trackcomponent
 */
export const Wrapper = styled.div<{
  $padding: string;
  $justify: JustifyValues;
  $align: AlignValues;
  $hasSideImage: boolean;
  $isMobile: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) => $align};
  justify-content: ${({ $justify }) => $justify};
  overflow: inherit;
  height: 100%;
  max-height: ${({ $isMobile }) => ($isMobile ? "inherit" : "80vh")};
  width: 100%;
  box-sizing: border-box;
  padding: ${({ $padding }) => $padding};
  ${({ $hasSideImage }) => $hasSideImage && "padding-left: 50%"};
`;

const ALL_VIEWPORTS_NAVIGATION_BLOCK_SIZE = 96;
// Image sizing
const DESKTOP_IMAGE_MAX_INLINE_SIZE = 375;
const MOBILE_IMAGE_BLOCK_SIZE = 168;

/**
 * @link [Docs](https://slate.faire.team/slate/component/modal)
 * @trackcomponent
 */
export const Modal: React.FC<ModalProps> = ({
  primary,
  padding,
  children,
  onClose,
  closeIconProps = {},
  showCloseButton = true,
  closeButtonVariant,
  onBack,
  backIconProps = {},
  onForward,
  forwardIconProps = {},
  stickyNav = false,
  width,
  height,
  className,
  parentElement,
  contentStyle,
  overlayStyle,
  shouldReturnFocusAfterClose,
  shouldCloseOnOverlayClick,
  justify = "flex-start",
  align = "center",
  ariaHideApp,
  ["data-test-id"]: dataTestId,
  setContentRef,
  postEventModalId,
  isOpen = true,
  allowBackgroundScroll,
  noPortal,
  ["aria-labelledby"]: ariaLabelledBy,
  ["aria-describedby"]: ariaDescribedBy,
  image,
  imageAlt,
}) => {
  const postedEvent = useRef(false);
  const shouldPopScrollStore = useRef(false);
  const isSSR = useIsSSR();

  const postShowModalEvent = useCallback(() => {
    postedEvent.current = true;
    if (postEventModalId) {
      trackEvent(`/ui/modal/show/${postEventModalId}`);
      trackSlateDesignSystemUiModalShowView(postEventModalId);
    }
  }, [postEventModalId]);

  useEffect(() => {
    if (isOpen && !postedEvent.current) {
      postShowModalEvent();
    }
  }, [isOpen, postShowModalEvent]);

  const handleAfterOpen = () => {
    if (!allowBackgroundScroll) {
      ScrollStore.get().pushNoScroll();
      shouldPopScrollStore.current = true;
    }
  };

  const handleAfterClose = () => {
    maybePopScrollStore();
  };

  const maybePopScrollStore = () => {
    if (shouldPopScrollStore.current) {
      ScrollStore.get().popNoScroll();
      shouldPopScrollStore.current = false;
    }
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    // React synthetic events bubble through portals.
    // stopPropagation() will stop click events from bubbling up to the parent component.
    e.stopPropagation();
  };

  const viewport = useViewport();

  const [showDivider, setShowDivider] = useState(false);
  const [navigationOpacity, setNavigationOpacity] = useState(image ? 0 : 1);

  const { scrollState, refCallBack } = useDetectRefScroll("y", 0);
  useEffect(() => {
    setShowDivider(scrollState.scrollPosition > 0);

    setNavigationOpacity(
      image && viewport.isMobile
        ? Math.min(
            Math.round(
              (scrollState.scrollPosition /
                ALL_VIEWPORTS_NAVIGATION_BLOCK_SIZE) *
                100
            ) / 100,
            1
          )
        : 1
    );
  }, [image, viewport, scrollState, showDivider]);

  const [imageError, setImageError] = useState(false);

  const onImageError = () => {
    if (!imageError) {
      setImageError(true);
    }
  };

  const theme = useTheme();
  const nestedTheme: ITheme = {
    ...theme,
    buttons: theme.buttons,
  };
  const navProps = {
    onClose: onClose,
    onBack: onBack,
    onForward: onForward,
    primary: primary,
    showCloseButton: showCloseButton,
    closeButtonVariant: closeButtonVariant,
    closeIconProps: closeIconProps,
    backIconProps: backIconProps,
    forwardIconProps: forwardIconProps,
    showDivider: showDivider,
    hasImage: !!image,
    isMobile: viewport.isMobile,
    navigationOpacity: navigationOpacity,
  };

  const hasSideImage = !viewport.isMobile && !!image && !imageError;
  const hasHeaderImage = !!image && viewport.isMobile && !imageError;

  return (
    <ThemeProvider theme={nestedTheme}>
      <BaseModal
        contentRef={setContentRef}
        ariaHideApp={ariaHideApp}
        primary={primary}
        className={className}
        isOpen={isOpen}
        shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
        onAfterClose={handleAfterClose}
        onAfterOpen={handleAfterOpen}
        parentSelector={
          parentElement === undefined ? undefined : () => parentElement
        }
        style={{
          content: {
            ...useGetModalSizing(
              hasSideImage || hasHeaderImage,
              viewport.isMobile,
              height,
              width
            ),
            ...(contentStyle ?? {}),
          },
          overlay: {
            ...(noPortal
              ? {
                  position: "fixed",
                  width: "100vw",
                  height: "100vh",
                  top: 0,
                  left: 0,
                }
              : {}),
            ...(overlayStyle ?? {}),
          },
        }}
        onRequestClose={onClose}
        data-test-id="faireModal"
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        noPortal={noPortal}
        aria={{
          labelledby: ariaLabelledBy,
          describedby: ariaDescribedBy,
        }}
      >
        <Wrapper
          onClick={handleWrapperClick}
          data-test-id={dataTestId}
          $padding={padding ?? (viewport.isMobile ? "64px 12px" : "30px 57px")}
          $justify={justify}
          $align={align}
          ref={refCallBack}
          $hasSideImage={hasSideImage}
          $isMobile={viewport.isMobile}
        >
          {hasSideImage && (
            <RegularSideImage
              alt={imageAlt || image.alt_text || ""} // If no imageAlt is provided, hide image from screenreaders
              onError={onImageError}
              src={optimizeImageUrlWidth(
                image,
                isSSR,
                DESKTOP_IMAGE_MAX_INLINE_SIZE
              )}
            />
          )}
          {stickyNav ? (
            <ModalNavigationFixed {...navProps} />
          ) : (
            <ModalNavigationAbsolute {...navProps} />
          )}
          {hasHeaderImage && (
            <MobileHeaderImage
              alt={imageAlt || image.alt_text || ""} // If no imageAlt is provided, hide image from screenreaders
              onError={onImageError}
              src={optimizeImageUrlHeight(
                image,
                isSSR,
                MOBILE_IMAGE_BLOCK_SIZE
              )}
            />
          )}
          {children}
        </Wrapper>
      </BaseModal>
    </ThemeProvider>
  );
};

export const MobileHeaderImage = styled.img`
  block-size: ${MOBILE_IMAGE_BLOCK_SIZE}px;
  inline-size: 100%;
  object-fit: cover;
`;

export const RegularSideImage = styled.img`
  block-size: 100%;
  inline-size: 50%;
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  object-fit: cover;
`;
